import React, { type Dispatch, type SetStateAction } from "react";
import { ephemeralStore, ticketViewerSlice } from "../../../store/ephemeral";
import { type ItineraryDTO, type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import { ItineraryDebugDetailsToolbar } from "../../Debug/itineraryDebug";
import ItineraryDetailsNavigateCta from "../itineraryDetails/ItineraryDetailsNavigateCta";
import ItineraryDetailsPanel from "../itineraryDetails/ItineraryDetailsPanel";
import { type ItineraryDetailsOfferLinkDisplay } from "../itineraryDetails/offerPricing/OfferPricingFacade";
import ItineraryPanelChromeRow from "../itineraryPanel/ItineraryPanelChromeRow";
import { type PanelResizeLayout } from "../panelResize";
import { type ItineraryPanelViewDirection } from "./ItineraryItemMotion";
import { type OfferFetchStatus } from "./itineraryList.tools";

interface ItineraryDetailsChromeData {
    "computedOfferTotalPrice":          number | null;
    "showIncompleteOfferTotalFootnote": boolean;
    "rentalItineraryOfferLink":         ItineraryDetailsOfferLinkDisplay | null;
}

interface ItineraryDetailsPanelViewProps {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Whether prices display in EUR. */
    "isEuro": boolean;

    /** Shared resize layout measurements from Home. */
    "panelResizeLayout": PanelResizeLayout;

    /** Chrome pricing / rental-link display data, or null when no itinerary is selected. */
    "itineraryDetailsChromeData": ItineraryDetailsChromeData | null;

    /** Offer fetch status for the selected itinerary. */
    "offerStatus": OfferFetchStatus;

    /** Whether the selected itinerary is already saved. */
    "isSelectedItinerarySaved": boolean;

    /** Whether the selected itinerary carries purchased tickets (hides the save toggle). */
    "isPurchasedItinerary"?: boolean;

    /** Whether price chrome should show in details (hidden in saved mode). */
    "showPrice": boolean;

    /** Currently selected itinerary (details view is only mounted when one exists). */
    "selectedItinerary": ItineraryDTO;

    /** Clears map leg selection when leaving itinerary details. */
    "handleResetMap": () => void;

    /** Resets the panel to the expanded snap tier. */
    "onResetPanelExpanded": () => void;

    /** Restores the last remembered itinerary-list snap tier. */
    "onRestoreItineraryListPanelSnap": () => void;

    /** Toggles saved state for the selected itinerary; omit to hide the save toggle. */
    "onSaveToggle"?: () => void;

    /** Pricing preferences for chrome and leg pricing. */
    "pricingPreferences": ItineraryPricingPreferencesDTO;

    /** Setter for backup return car routes. */
    "setBackupReturnItineraries": ContainerProps["setBackupReturnItineraries"];

    /** Setter for outbound car routes. */
    "setGoItineraries": ContainerProps["setGoItineraries"];

    /** Setter for MyCar selection mode. */
    "setITakeMyCar": Dispatch<SetStateAction<boolean>>;

    /** Setter for car itineraries used by chrome back navigation. */
    "setCarItineraries": ContainerProps["setCarItineraries"];

    /** Updates list↔details motion direction. */
    "setItineraryPanelViewDirection": Dispatch<SetStateAction<ItineraryPanelViewDirection>>;

    /** Setter for the selected itinerary. */
    "setSelectedItinerary": Dispatch<SetStateAction<ItineraryDTO | null>>;

    /** Setter for the top arrow visibility. */
    "setShowArrowTop": Dispatch<SetStateAction<boolean>>;

    /** Enables itinerary debug tooling in details. */
    "debugItineraries": boolean;

    /** Whether details debug actions are disabled. */
    "isDetailsDebugActionDisabled": boolean;

    /** Relaunches offer fetch for debug. */
    "onRelaunchOffer": () => void;

    /** Relaunches segmentation and offer for debug. */
    "onRelaunchSegmentationAndOffer": () => void;

    /** Whether the navigate CTA is shown (backend runtime feature flag). */
    "showNavigationAction": boolean;

    /** Whether navigation session is currently following. */
    "isFollowingNavigation": boolean;

    /** Begin-navigation callback. */
    "onNavigate": () => void;

    /** Departure address labels for the details timeline. */
    "departureSelectedAddress"?: {
        "result"?:  string | null;
        "address"?: string | null;
    } | null;

    /** Destination address labels for the details timeline. */
    "selectedAddress"?: {
        "result"?:  string | null;
        "address"?: string | null;
    } | null;

    /** Focuses the map on a point. */
    "focusMapOnPoint": ContainerProps["focusMapOnPoint"];

    /** Whether leg details should be visible during panel resize. */
    "legDetails": boolean;

    /** Currently selected itinerary leg. */
    "selectedLeg": ItineraryLegDTO | null;

    /** Setter for the selected itinerary leg. */
    "setSelectedLeg": Dispatch<SetStateAction<ItineraryLegDTO | null>>;
}

/**
 * Presentational itinerary-details panel (chrome, debug, navigate CTA, details body).
 *
 * @param props Details panel composition props.
 * @returns Details panel layout root.
 */
const ItineraryDetailsPanelView: React.FC<ItineraryDetailsPanelViewProps> = ( {
    isDarkUi,
    isEuro,
    panelResizeLayout,
    itineraryDetailsChromeData,
    offerStatus,
    isSelectedItinerarySaved,
    isPurchasedItinerary = false,
    showPrice,
    selectedItinerary,
    handleResetMap,
    onResetPanelExpanded,
    onRestoreItineraryListPanelSnap,
    onSaveToggle,
    pricingPreferences,
    setBackupReturnItineraries,
    setGoItineraries,
    setITakeMyCar,
    setCarItineraries,
    setItineraryPanelViewDirection,
    setSelectedItinerary,
    setShowArrowTop,
    debugItineraries,
    isDetailsDebugActionDisabled,
    onRelaunchOffer,
    onRelaunchSegmentationAndOffer,
    showNavigationAction,
    isFollowingNavigation,
    onNavigate,
    departureSelectedAddress,
    selectedAddress,
    focusMapOnPoint,
    legDetails,
    selectedLeg,
    setSelectedLeg,
} ) => {
    return (
        <div
            className="flex min-h-0 flex-1 flex-col"
            id="itinerary-details-layout"
        >
            <ItineraryPanelChromeRow
                computedOfferTotalPrice={itineraryDetailsChromeData?.computedOfferTotalPrice ?? null}
                handleResetMap={handleResetMap}
                isDarkUi={isDarkUi}
                isEuro={isEuro}
                isPurchasedItinerary={isPurchasedItinerary}
                isSaved={isSelectedItinerarySaved}
                mode="itineraryDetails"
                offerStatus={offerStatus}
                onResetPanelExpanded={onResetPanelExpanded}
                onRestoreItineraryListPanelSnap={onRestoreItineraryListPanelSnap}
                onSaveToggle={isPurchasedItinerary ? undefined : onSaveToggle}
                onShowPurchasedTickets={() => ephemeralStore.setValue( ticketViewerSlice, {
                    "isOpen":           true,
                    "itineraryId":      selectedItinerary.id,
                    "initialSegmentId": null,
                } )}
                panelResizeLayout={panelResizeLayout}
                pricingPreferences={pricingPreferences}
                rentalItineraryOfferLink={itineraryDetailsChromeData?.rentalItineraryOfferLink ?? null}
                selectedItinerary={selectedItinerary}
                setBackupReturnItineraries={setBackupReturnItineraries}
                setGoItineraries={setGoItineraries}
                setITakeMyCar={setITakeMyCar}
                setItineraries={setCarItineraries}
                setItineraryPanelViewDirection={setItineraryPanelViewDirection}
                setSelectedItinerary={setSelectedItinerary}
                setShowArrowTop={setShowArrowTop}
                showIncompleteOfferTotalFootnote={itineraryDetailsChromeData?.showIncompleteOfferTotalFootnote ?? false}
                showPrice={showPrice}
            />
            <ItineraryDebugDetailsToolbar
                debugItineraries={debugItineraries}
                isDarkUi={isDarkUi}
                isDisabled={isDetailsDebugActionDisabled}
                onRelaunchOffer={onRelaunchOffer}
                onRelaunchSegmentationAndOffer={onRelaunchSegmentationAndOffer}
            />
            {showNavigationAction
                ? (
                    <ItineraryDetailsNavigateCta
                        isDarkUi={isDarkUi}
                        isFollowingNavigation={isFollowingNavigation}
                        onNavigate={onNavigate}
                    />
                )
                : null}
            <ItineraryDetailsPanel
                departureSelectedAddress={departureSelectedAddress}
                focusMapOnPoint={focusMapOnPoint}
                isDarkUi={isDarkUi}
                isEuro={isEuro}
                isPurchasedItinerary={isPurchasedItinerary}
                legDetails={legDetails}
                offerStatus={offerStatus}
                pricingPreferences={pricingPreferences}
                selectedAddress={selectedAddress}
                selectedItinerary={selectedItinerary}
                selectedLeg={selectedLeg}
                setSelectedLeg={setSelectedLeg}
                showPrice={showPrice}
            />
        </div>
    );
};

export default ItineraryDetailsPanelView;
